import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase as _supabase } from '@/integrations/supabase/client';
import { useCurrentAppUser } from './useCurrentAppUser';
import { useTwilio } from '@/contexts/TwilioContext';
import { toast } from 'sonner';

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const supabase = _supabase as any;

interface EdgeErrorPayload {
  error?: string;
}

async function getEdgeErrorMessage(
  error: unknown,
  data: EdgeErrorPayload | null | undefined,
  fallback: string,
): Promise<string> {
  if (data?.error) return data.error;

  try {
    const context = (error as { context?: Response } | null)?.context;
    if (context && typeof context.clone === 'function') {
      const payload = (await context.clone().json()) as EdgeErrorPayload;
      if (payload?.error) return payload.error;
    }
  } catch (parseError) {
    console.warn('[PowerDialer] Não foi possível ler o erro do servidor:', parseError);
  }

  return (error as { message?: string } | null)?.message || fallback;
}

export type DialerSessionStatus = 'idle' | 'active' | 'paused' | 'completed' | 'ended';
export type AttemptStatus = 'dialing' | 'ringing' | 'connected' | 'completed';

export interface DialerSession {
  id: string;
  sdr_id: string;
  status: DialerSessionStatus;
  concurrency: number;
  conference_name: string;
  from_e164: string | null;
  batch_label: string | null;
  ring_timeout_seconds: number;
  total_dialed: number;
  total_human: number;
  total_machine: number;
  total_connected: number;
  queue_contact_ids: string[];
  started_at: string | null;
  ended_at: string | null;
}

export interface DialerAttempt {
  id: string;
  session_id: string;
  contact_id: string | null;
  assignment_id: string | null;
  to_e164: string;
  status: AttemptStatus;
  outcome: string | null;
  answered_by: string | null;
  amd_reason?: string | null;
  amd_false_positive?: boolean | null;

  connected_at: string | null;
  ended_at: string | null;
  duration_seconds: number | null;
  created_at: string;
  contact?: {
    id: string;
    first_name: string | null;
    last_name: string | null;
  } | null;
}

/** Sessão ativa/pausada do SDR logado. */
export function useDialerSession() {
  const { data: currentUser } = useCurrentAppUser();
  const sdrId = currentUser?.id;

  return useQuery({
    queryKey: ['dialer-session', sdrId],
    enabled: !!sdrId,
    refetchInterval: 4000,
    queryFn: async (): Promise<DialerSession | null> => {
      const { data, error } = await supabase
        .from('dialer_sessions')
        .select('*')
        .eq('sdr_id', sdrId)
        .in('status', ['active', 'paused'])
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle();
      if (error) throw error;
      return data ?? null;
    },
  });
}

/** Tentativas recentes da sessão (slots ao vivo + histórico curto). */
export function useDialerAttempts(sessionId: string | null | undefined) {
  return useQuery({
    queryKey: ['dialer-attempts', sessionId],
    enabled: !!sessionId,
    refetchInterval: 1500,
    queryFn: async (): Promise<DialerAttempt[]> => {
      const { data, error } = await supabase
        .from('dialer_attempts')
        .select(
          `id, session_id, contact_id, assignment_id, to_e164, status, outcome, answered_by, amd_reason, amd_false_positive,
           connected_at, ended_at, duration_seconds, created_at,
           contact:contacts!dialer_attempts_contact_id_fkey(id, first_name, last_name)`

        )
        .eq('session_id', sessionId)
        .order('created_at', { ascending: false })
        .limit(40);
      if (error) throw error;
      return data ?? [];
    },
  });
}


/** Controle completo do Power Dialer (start/pause/resume/skip/end + auto dial-next). */
export function usePowerDialer(options: { autoManage?: boolean } = {}) {
  const autoManage = options.autoManage ?? true;
  const queryClient = useQueryClient();
  const { joinDialerConference, currentCall, hangUp, toggleMute, sendDtmf, isDialerAudioReady } = useTwilio();
  const { data: session, refetch: refetchSession } = useDialerSession();
  const { data: attempts = [] } = useDialerAttempts(session?.id ?? null);

  const [isBusy, setIsBusy] = useState(false);
  const joinedConference = useRef<string | null>(null);
  const dialingRef = useRef(false);
  const completedAttemptsRef = useRef<Set<string>>(new Set());

  // Linha considerada "presa" quando passa do timeout de toque + margem:
  // libera o slot no cliente para o discador continuar reabastecendo.
  const staleAfterMs = ((session?.ring_timeout_seconds ?? 40) + 5) * 1000;
  const liveAttempts = useMemo(
    () =>
      attempts.filter((a) => {
        if (a.status === 'connected') return true;
        if (a.status !== 'dialing' && a.status !== 'ringing') return false;
        return Date.now() - new Date(a.created_at).getTime() < staleAfterMs;
      }),
    [attempts, staleAfterMs]
  );
  const connectedAttempt = useMemo(() => attempts.find((a) => a.status === 'connected') ?? null, [attempts]);


  const invalidate = useCallback(() => {
    queryClient.invalidateQueries({ queryKey: ['dialer-session'] });
    queryClient.invalidateQueries({ queryKey: ['dialer-attempts'] });
    queryClient.invalidateQueries({ queryKey: ['outbound-queue'] });
    queryClient.invalidateQueries({ queryKey: ['outbound-stats-db'] });
    queryClient.invalidateQueries({ queryKey: ['outbound-batches'] });
  }, [queryClient]);

  // Webhooks do Twilio concluem tentativas fora do ciclo de mutations do React Query.
  // Ao detectar uma nova conclusão no polling, sincroniza imediatamente fila e contadores.
  useEffect(() => {
    const completedIds = new Set(
      attempts.filter((attempt) => attempt.status === 'completed').map((attempt) => attempt.id),
    );
    const hasNewCompletion = [...completedIds].some((id) => !completedAttemptsRef.current.has(id));
    completedAttemptsRef.current = completedIds;
    if (hasNewCompletion) invalidate();
  }, [attempts, invalidate]);

  const callControl = useCallback(
    async (
      action:
        | 'pause'
        | 'resume'
        | 'skip'
        | 'hangup'
        | 'end'
        | 'hold'
        | 'unhold'
        | 'transfer'
        | 'transfer-status'
        | 'transfer-complete'
        | 'transfer-cancel',
      extra?: Record<string, unknown>,
    ) => {
      setIsBusy(true);
      try {
        const { data, error } = await supabase.functions.invoke('power-dialer-control', {
          body: { action, ...(extra ?? {}) },
        });
        if (error) throw error;
        if (data?.error) throw new Error(data.error);
        if (action === 'end') {
          joinedConference.current = null;
          if (currentCall) hangUp();
        }
        invalidate();
        return data;
      } catch (err) {
        console.error('[PowerDialer] control error:', err);
        toast.error((err as Error).message || 'Erro ao controlar o discador');
      } finally {
        setIsBusy(false);
        refetchSession();
      }
    },
    [currentCall, hangUp, invalidate, refetchSession]
  );

  const start = useCallback(
    async (contactIds: string[], options?: { concurrency?: number; batchLabel?: string | null }) => {
      if (!contactIds.length) {
        toast.error('Nenhum contato elegível na fila');
        return;
      }
      setIsBusy(true);
      try {
        // 1) prepara a sessão sem ligar para ninguém
        const { data, error } = await supabase.functions.invoke('power-dialer-start', {
          body: {
            action: 'start',
            contactIds,
            concurrency: options?.concurrency ?? 3,
            batchLabel: options?.batchLabel ?? null,
            prepareOnly: true,
          },
        });
        if (error) {
          throw new Error(await getEdgeErrorMessage(error, data, 'Erro ao preparar o Power Dialer'));
        }
        if (data?.error) throw new Error(data.error);

        const conference = data?.conference ?? data?.session?.conference_name;
        if (conference && joinedConference.current !== conference) {
          joinedConference.current = conference;
          const agentCall = await joinDialerConference(conference);
          if (!agentCall) {
            await supabase.functions.invoke('power-dialer-control', { body: { action: 'end' } });
            joinedConference.current = null;
            throw new Error('Áudio do SDR não conectou. Nenhum cliente foi discado.');
          }
        }
        // 2) somente após o áudio confirmado, abre os slots de clientes
        const { error: resumeError } = await supabase.functions.invoke('power-dialer-control', {
          body: { action: 'resume' },
        });
        if (resumeError) throw resumeError;

        const { data: dialData, error: dialError } = await supabase.functions.invoke('power-dialer-start', {
          body: { action: 'dial-next' },
        });
        if (dialError) {
          throw new Error(await getEdgeErrorMessage(dialError, dialData, 'Erro ao iniciar as ligações'));
        }
        if (dialData?.error) throw new Error(dialData.error);
        toast.success(`Power Dialer iniciado (${dialData?.dialed ?? 0} ligações)`);
        invalidate();
      } catch (err) {
        console.error('[PowerDialer] start error:', err);
        toast.error((err as Error).message || 'Erro ao iniciar o Power Dialer');
      } finally {
        setIsBusy(false);
        refetchSession();
      }
    },
    [invalidate, joinDialerConference, refetchSession]
  );

  const dialNext = useCallback(async () => {
    if (dialingRef.current) return;
    dialingRef.current = true;
    try {
      const { data, error } = await supabase.functions.invoke('power-dialer-start', { body: { action: 'dial-next' } });
      if (error) {
        throw new Error(await getEdgeErrorMessage(error, data, 'Erro ao buscar a próxima ligação'));
      }
      invalidate();
    } catch (err) {
      console.error('[PowerDialer] dial-next error:', err);
    } finally {
      dialingRef.current = false;
    }
  }, [invalidate]);

  // Reabastece slots automaticamente: mantém sempre `concurrency` linhas em voo
  useEffect(() => {
    if (!autoManage) return;
    if (!session || session.status !== 'active') return;
    if (connectedAttempt) return;
    if (liveAttempts.length >= (session.concurrency ?? 3)) return;

    const timer = setTimeout(() => { void dialNext(); }, 600);
    return () => clearTimeout(timer);
  }, [autoManage, session, connectedAttempt, liveAttempts.length, dialNext]);


  // Garante que o SDR esteja na conferência enquanto a sessão está ativa
  useEffect(() => {
    if (!autoManage) return;
    if (!session || session.status !== 'active') return;
    if (currentCall) return;
    if (joinedConference.current === session.conference_name) return;
    let cancelled = false;
    const reconnect = async () => {
      const call = await joinDialerConference(session.conference_name);
      if (cancelled) return;
      if (call) {
        joinedConference.current = session.conference_name;
      } else {
        joinedConference.current = null;
        toast.error('Áudio do Power Dialer desconectado. A sessão foi pausada por segurança.');
        await callControl('pause');
      }
    };
    void reconnect();
    return () => { cancelled = true; };
  }, [autoManage, session, currentCall, joinDialerConference, callControl]);

  // Estado da transferência anunciada (destino chamando / atendeu / indisponível)
  const [transferState, setTransferState] = useState<{
    status: 'calling_target' | 'target_connected' | 'target_unavailable' | null;
    mode: string | null;
    targetUserId: string | null;
  } | null>(null);

  const refreshTransfer = useCallback(async () => {
    try {
      const { data } = await supabase.functions.invoke('power-dialer-control', {
        body: { action: 'transfer-status' },
      });
      setTransferState(data?.transfer ?? null);
    } catch {
      /* poll silencioso */
    }
  }, []);

  useEffect(() => {
    if (!session || !connectedAttempt) {
      setTransferState(null);
      return;
    }
    if (!transferState && !connectedAttempt) return;
    const timer = setInterval(() => { void refreshTransfer(); }, 2500);
    return () => clearInterval(timer);
  }, [session, connectedAttempt, transferState, refreshTransfer]);

  const startTransfer = useCallback(
    async (targetUserId: string, mode: 'warm' | 'cold') => {
      const data = await callControl('transfer', { targetUserId, mode });
      if (data?.ok && mode === 'warm') {
        setTransferState({ status: 'calling_target', mode: 'warm', targetUserId });
      }
      return data;
    },
    [callControl],
  );

  const completeTransfer = useCallback(async () => {
    const data = await callControl('transfer-complete');
    if (data?.ok) setTransferState(null);
    return data;
  }, [callControl]);

  const cancelTransfer = useCallback(async () => {
    const data = await callControl('transfer-cancel');
    if (data?.ok) setTransferState(null);
    return data;
  }, [callControl]);

  return {
    session: session ?? null,
    attempts,
    liveAttempts,
    connectedAttempt,
    isDialerAudioReady,
    isBusy,
    start,
    dialNext,
    pause: () => callControl('pause'),
    resume: () => callControl('resume'),
    skip: () => callControl('skip'),
    hangupConnected: () => callControl('hangup'),
    holdConnected: () => callControl('hold'),
    unholdConnected: () => callControl('unhold'),
    transferConnected: (targetUserId: string) => callControl('transfer', { targetUserId, mode: 'warm' }),
    transferState,
    startTransfer,
    completeTransfer,
    cancelTransfer,
    sendDtmf,
    toggleMute,
    end: () => callControl('end'),
  };
}

